import {
    CheckCircleOutline,
    Delete,
    Edit,
    ErrorOutline,
} from "@mui/icons-material";
import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormLabel,
    IconButton,
    Radio,
    RadioGroup,
    Tooltip,
    Typography,
} from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import AcxChip from "components/UI/AcxChip";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxTable, { IAcxTableColumn } from "components/UI/AcxTable/AcxTable";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import AcxSelect from "components/UI/Select/BaseSelectComponents/AcxSelect";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import { observer } from "mobx-react";
import type { LLMDeploymentViewModel } from "models/LLMs/LLMDeploymentViewModel";
import {
    CreateLLMModelPayload,
    DeploymentConfigTestPayload,
    LLMModelPurpose,
    type LLMManagementModelViewModel,
} from "models/LLMs/LLMManagementModelViewModel";
import type { TestResultViewModel } from "models/LLMs/TestResultViewModel";
import React, { useMemo, useRef, useState } from "react";
import { ServiceError } from "services/BaseService";
import type LLMManagementStore from "stores/LLMManagementStore";
import theme from "Theme/AppTheme";
import { useStore } from "utils/useStore";
import AddButton from "../OrganizationDataSources/components/AddButton";

interface ModelFormState {
    name: string;
    underlyingModelName: string;
    isThinkingModel: boolean;
    thinkingEffortLevels: string;
    purpose: LLMModelPurpose;
}

const emptyModelForm: ModelFormState = {
    name: "",
    underlyingModelName: "",
    isThinkingModel: false,
    thinkingEffortLevels: "none,low,medium,high,xhigh",
    purpose: LLMModelPurpose.ModelBuilder,
};

interface DeploymentFormState {
    modelId: string;
    hostId: string;
    priority: number;
    configurationValue: string;
}

const emptyDeploymentForm: DeploymentFormState = {
    modelId: "",
    hostId: "",
    priority: 0,
    configurationValue: "",
};

interface InitialDeploymentFormState {
    hostId: string;
    priority: number;
    configurationValue: string;
}

const emptyInitialDeploymentForm: InitialDeploymentFormState = {
    hostId: "",
    priority: 0,
    configurationValue: "",
};

function isSelfHostedHost(hostType: string | undefined) {
    return hostType === "AcxSelfHosted";
}

function getDeploymentConfigLabel(hostType: string | undefined) {
    return isSelfHostedHost(hostType) ? "Endpoint URL" : "Deployment Name";
}

function getDeploymentConfigHelperText(hostType: string | undefined) {
    return isSelfHostedHost(hostType)
        ? "OpenAI-compatible base URL, usually ending in /v1"
        : "Azure deployment name";
}

function parseThinkingEffortLevels(value: string) {
    return value
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
}

function getErrorMessage(error: unknown) {
    if (error instanceof ServiceError) {
        return error.errorMessage ?? error.message;
    }
    return error instanceof Error ? error.message : "Something went wrong";
}

function buildDeploymentPayload(
    form: DeploymentFormState,
    hostType: string | undefined,
) {
    const configuration = isSelfHostedHost(hostType)
        ? { EndpointUrl: form.configurationValue }
        : { DeploymentName: form.configurationValue };
    return {
        modelId: form.modelId,
        hostId: form.hostId,
        priority: form.priority,
        configuration,
    };
}

function buildInitialDeploymentTestPayload(
    form: InitialDeploymentFormState,
    modelForm: ModelFormState,
    hostType: string | undefined,
): DeploymentConfigTestPayload {
    const configuration = isSelfHostedHost(hostType)
        ? { EndpointUrl: form.configurationValue }
        : { DeploymentName: form.configurationValue };
    return {
        hostId: form.hostId,
        underlyingModelName: modelForm.underlyingModelName,
        priority: form.priority,
        isThinkingModel: modelForm.isThinkingModel,
        thinkingEffortLevels: modelForm.isThinkingModel
            ? parseThinkingEffortLevels(modelForm.thinkingEffortLevels)
            : null,
        configuration,
    };
}

interface Props {
    store: LLMManagementStore;
}

const LLMModelsTable: React.FC<Props> = observer(({ store }) => {
    const messageStore = useStore(MessageStore);

    // Model state
    const [modelDialogOpen, setModelDialogOpen] = useState(false);
    const [editingModel, setEditingModel] =
        useState<LLMManagementModelViewModel | null>(null);
    const [modelForm, setModelForm] = useState<ModelFormState>(emptyModelForm);
    const [modelSaving, setModelSaving] = useState(false);
    const [deleteModel, setDeleteModel] =
        useState<LLMManagementModelViewModel | null>(null);

    // Initial deployment state (used only in Create mode)
    const [initialDeployForm, setInitialDeployForm] =
        useState<InitialDeploymentFormState>(emptyInitialDeploymentForm);
    const [initialDeployTestResult, setInitialDeployTestResult] =
        useState<TestResultViewModel | null>(null);
    const [initialDeployTesting, setInitialDeployTesting] = useState(false);
    const initialDeployTestVersion = useRef(0);

    const getHostType = (hostId: string) =>
        store.hosts.find((host) => host.id === hostId)?.type;

    // Deployment state (for adding subsequent deployments to existing model)
    const [deployDialogOpen, setDeployDialogOpen] = useState(false);
    const [editingDeployment, setEditingDeployment] =
        useState<LLMDeploymentViewModel | null>(null);
    const [deployForm, setDeployForm] =
        useState<DeploymentFormState>(emptyDeploymentForm);
    const [deploySaving, setDeploySaving] = useState(false);
    const [deleteDeployment, setDeleteDeployment] =
        useState<LLMDeploymentViewModel | null>(null);
    const [deployTestResult, setDeployTestResult] =
        useState<TestResultViewModel | null>(null);
    const [deployTesting, setDeployTesting] = useState(false);
    // Bumped on every form mutation; in-flight tests check this to ignore
    // stale responses that would otherwise re-enable Save for changed fields.
    const deployTestVersion = useRef(0);

    // Model actions
    const resetInitialDeployTest = () => {
        initialDeployTestVersion.current += 1;
        setInitialDeployTestResult(null);
    };

    const openCreateModel = () => {
        setEditingModel(null);
        setModelForm(emptyModelForm);
        setInitialDeployForm(emptyInitialDeploymentForm);
        setInitialDeployTestResult(null);
        initialDeployTestVersion.current = 0;
        setModelDialogOpen(true);
    };

    const openEditModel = (model: LLMManagementModelViewModel) => {
        setEditingModel(model);
        setModelForm({
            name: model.name ?? "",
            underlyingModelName: model.underlyingModelName ?? "",
            isThinkingModel: model.isThinkingModel ?? false,
            thinkingEffortLevels: model.thinkingEffortLevels?.join(",") ?? "",
            purpose: model.purpose ?? LLMModelPurpose.ModelBuilder,
        });
        setModelDialogOpen(true);
    };

    const closeModelDialog = () => {
        setModelDialogOpen(false);
    };

    const updateInitialDeployForm = (
        updater: (f: InitialDeploymentFormState) => InitialDeploymentFormState,
    ) => {
        setInitialDeployForm((f) => updater(f));
        resetInitialDeployTest();
    };

    const handleTestInitialDeployment = async () => {
        const version = ++initialDeployTestVersion.current;
        setInitialDeployTesting(true);
        try {
            const result = await store.testLLMDeploymentConfig(
                buildInitialDeploymentTestPayload(
                    initialDeployForm,
                    modelForm,
                    getHostType(initialDeployForm.hostId),
                ),
            );
            if (version !== initialDeployTestVersion.current) return;
            setInitialDeployTestResult(result);
        } catch {
            if (version !== initialDeployTestVersion.current) return;
            setInitialDeployTestResult({
                success: false,
                message: "Test request failed",
                latencyMs: null,
            });
        } finally {
            if (version === initialDeployTestVersion.current) {
                setInitialDeployTesting(false);
            }
        }
    };

    const handleSaveModel = async () => {
        setModelSaving(true);
        try {
            if (editingModel) {
                const payload: Omit<
                    LLMManagementModelViewModel,
                    "id" | "isActive"
                > = {
                    name: modelForm.name,
                    underlyingModelName: modelForm.underlyingModelName,
                    isThinkingModel: modelForm.isThinkingModel,
                    thinkingEffortLevels: modelForm.isThinkingModel
                        ? parseThinkingEffortLevels(
                              modelForm.thinkingEffortLevels,
                          )
                        : null,
                    purpose: modelForm.purpose,
                };
                const updated = await store.updateLLMModel(
                    editingModel.id,
                    payload,
                );
                messageStore.logMessage("LLM model updated", "success");
                setEditingModel(updated);
            } else {
                const initialDeployment = buildDeploymentPayload(
                    { ...initialDeployForm, modelId: "" },
                    getHostType(initialDeployForm.hostId),
                );
                const payload: CreateLLMModelPayload = {
                    name: modelForm.name,
                    underlyingModelName: modelForm.underlyingModelName,
                    isThinkingModel: modelForm.isThinkingModel,
                    thinkingEffortLevels: modelForm.isThinkingModel
                        ? parseThinkingEffortLevels(
                              modelForm.thinkingEffortLevels,
                          )
                        : null,
                    purpose: modelForm.purpose,
                    initialDeployment: {
                        hostId: initialDeployment.hostId,
                        priority: initialDeployment.priority,
                        configuration: initialDeployment.configuration,
                    },
                };
                const created = await store.createLLMModel(payload);
                messageStore.logMessage("LLM model created", "success");
                if (created?.id) {
                    setEditingModel(created);
                }
            }
        } catch (error) {
            messageStore.logError(getErrorMessage(error));
        } finally {
            setModelSaving(false);
        }
    };

    const handleDeleteModel = async (id: string) => {
        try {
            await store.deleteLLMModel(id);
            messageStore.logMessage("LLM model deleted", "success");
        } catch (error) {
            messageStore.logError(getErrorMessage(error));
        } finally {
            setDeleteModel(null);
        }
    };

    // Deployment actions
    const resetDeployTest = () => {
        setDeployTestResult(null);
    };

    const openCreateDeployment = () => {
        if (!editingModel) return;
        setEditingDeployment(null);
        setDeployForm({ ...emptyDeploymentForm, modelId: editingModel.id });
        resetDeployTest();
        setDeployDialogOpen(true);
    };

    const openEditDeployment = (deployment: LLMDeploymentViewModel) => {
        setEditingDeployment(deployment);
        setDeployForm({
            modelId: deployment.modelId,
            hostId: deployment.hostId,
            priority: deployment.priority,
            configurationValue: String(
                deployment.configuration?.DeploymentName ??
                    deployment.configuration?.deploymentName ??
                    deployment.configuration?.EndpointUrl ??
                    deployment.configuration?.endpointUrl ??
                    "",
            ),
        });
        resetDeployTest();
        setDeployDialogOpen(true);
    };

    const updateDeployForm = (
        updater: (f: DeploymentFormState) => DeploymentFormState,
    ) => {
        setDeployForm((f) => updater(f));
        deployTestVersion.current += 1;
        resetDeployTest();
    };

    const handleTestDeploymentForm = async () => {
        const version = ++deployTestVersion.current;
        setDeployTesting(true);
        try {
            const result = await store.testLLMDeploymentConfig(
                buildDeploymentPayload(
                    deployForm,
                    getHostType(deployForm.hostId),
                ),
            );
            if (version !== deployTestVersion.current) return; // stale
            setDeployTestResult(result);
        } catch {
            if (version !== deployTestVersion.current) return; // stale
            setDeployTestResult({
                success: false,
                message: "Test request failed",
                latencyMs: null,
            });
        } finally {
            if (version === deployTestVersion.current) {
                setDeployTesting(false);
            }
        }
    };

    const handleSaveDeployment = async () => {
        setDeploySaving(true);
        try {
            const payload = buildDeploymentPayload(
                deployForm,
                getHostType(deployForm.hostId),
            );
            if (editingDeployment) {
                await store.updateLLMDeployment(editingDeployment.id, payload);
                messageStore.logMessage("Deployment updated", "success");
            } else {
                await store.createLLMDeployment(payload);
                messageStore.logMessage("Deployment created", "success");
            }
            setDeployDialogOpen(false);
        } catch (error) {
            messageStore.logError(getErrorMessage(error));
        } finally {
            setDeploySaving(false);
        }
    };

    const handleDeleteDeployment = async (id: string) => {
        try {
            await store.deleteLLMDeployment(id);
            messageStore.logMessage("Deployment deleted", "success");
        } catch (error) {
            messageStore.logError(getErrorMessage(error));
        } finally {
            setDeleteDeployment(null);
        }
    };

    // Model grid columns
    const modelColumns: IAcxTableColumn[] = useMemo(
        () => [
            {
                headerLabel: "Name",
                dataKey: "name",
            },
            {
                headerLabel: "Underlying Model",
                dataKey: "underlyingModelName",
            },
            {
                headerLabel: "Purpose",
                dataKey: "purpose",
                formatter: (_value, row: LLMManagementModelViewModel) => (
                    <AcxChip
                        label={
                            row.purpose === LLMModelPurpose.ModelBuilder
                                ? "Model Builder"
                                : row.purpose
                        }
                        size="small"
                        color={
                            row.purpose === LLMModelPurpose.KBo
                                ? "secondary800"
                                : "gray"
                        }
                    />
                ),
            },
            {
                headerLabel: "Thinking",
                dataKey: "isThinkingModel",
                formatter: (_value, row: LLMManagementModelViewModel) =>
                    row.isThinkingModel ? (
                        <AcxChip
                            label={
                                row.thinkingEffortLevels?.join(", ") ??
                                "Yes"
                            }
                            size="small"
                            color="lightBlue"
                        />
                    ) : (
                        <AcxChip label="No" size="small" color="gray" />
                    ),
            },
            {
                headerLabel: "Actions",
                dataKey: "actions",
                formatter: (_value, row: LLMManagementModelViewModel) => (
                    <Box
                        sx={{
                            display: "flex",
                            gap: 0.5,
                            justifyContent: "flex-end",
                            width: "100%",
                        }}
                    >
                        <Tooltip title="Edit">
                            <IconButton
                                size="small"
                                onClick={() => openEditModel(row)}
                            >
                                <Edit fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                            <IconButton
                                size="small"
                                onClick={() => setDeleteModel(row)}
                            >
                                <Delete fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                ),
            },
        ],
        [],
    );

    const deploymentColumns: IAcxTableColumn[] = [
        {
            headerLabel: "Host",
            dataKey: "hostName",
        },
        {
            headerLabel: "Deployment / Endpoint",
            dataKey: "configuration.DeploymentName",
            formatter: (_value, row: LLMDeploymentViewModel) =>
                String(
                    row.configuration?.DeploymentName ??
                        row.configuration?.deploymentName ??
                        row.configuration?.EndpointUrl ??
                        row.configuration?.endpointUrl ??
                        "",
                ),
        },
        {
            headerLabel: "Priority",
            dataKey: "priority",
        },
        {
            headerLabel: "Actions",
            dataKey: "actions",
            formatter: (_value, row: LLMDeploymentViewModel) => (
                <Box
                    sx={{
                        display: "flex",
                        gap: 0.5,
                        justifyContent: "flex-end",
                        width: "100%",
                    }}
                >
                    <Tooltip title="Edit">
                        <IconButton
                            size="small"
                            onClick={() => openEditDeployment(row)}
                        >
                            <Edit fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton
                            size="small"
                            onClick={() => setDeleteDeployment(row)}
                        >
                            <Delete fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
        },
    ];

    const thinkingEffortLevelsValid =
        !modelForm.isThinkingModel ||
        parseThinkingEffortLevels(modelForm.thinkingEffortLevels).length > 0;

    const modelFieldsValid =
        (modelForm.name ?? "").trim() !== "" &&
        (modelForm.underlyingModelName ?? "").trim() !== "" &&
        thinkingEffortLevelsValid;

    const initialDeployFieldsValid =
        initialDeployForm.hostId !== "" &&
        initialDeployForm.configurationValue.trim() !== "";

    const canTestInitialDeployment =
        initialDeployFieldsValid &&
        thinkingEffortLevelsValid &&
        !initialDeployTesting;

    const canSaveModel = editingModel
        ? modelFieldsValid
        : modelFieldsValid &&
          initialDeployFieldsValid &&
          initialDeployTestResult?.success === true;

    const canTestDeployment =
        deployForm.modelId !== "" &&
        deployForm.hostId !== "" &&
        deployForm.configurationValue.trim() !== "" &&
        !deployTesting;

    const canSaveDeployment =
        deployForm.modelId !== "" &&
        deployForm.hostId !== "" &&
        deployTestResult?.success === true;

    const hostOptions = useMemo(
        () =>
            store.hosts.map((h) => ({ value: h.id, label: h.name })),
        [store.hosts],
    );

    // Intentionally NOT memoized: `store.llmDeployments` is a MobX observable
    // mutated via push, so the array reference is stable and useMemo would
    // serve a stale list after creating a deployment from inside the dialog.
    const deploymentsForEditingModel = editingModel
        ? store.llmDeployments.filter((d) => d.modelId === editingModel.id)
        : [];

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Models Section */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        width: "100%",
                    }}
                >
                    <AddButton onClick={openCreateModel}>LLM Model</AddButton>
                </Box>
                <AcxTable
                    columns={modelColumns}
                    rows={store.llmModels}
                    enableHover
                    cellDense
                />
            </Box>

            {/* Model Create/Edit Dialog */}
            <AcxDialog
                title={editingModel ? "Edit LLM Model" : "Add LLM Model"}
                isOpen={modelDialogOpen}
                onClose={closeModelDialog}
                maxWidth={false}
                contentWidth="min(1200px, calc(100vw - 96px))"
                dialogContentChildren={
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                            pt: 1,
                        }}
                    >
                        <AcxMainTextField
                            id="model-name"
                            labelText="Name"
                            required
                            fullWidth
                            value={modelForm.name}
                            onChange={(e) =>
                                setModelForm((f) => ({
                                    ...f,
                                    name: e.target.value,
                                }))
                            }
                        />
                        <AcxMainTextField
                            id="model-underlying-name"
                            labelText="Underlying Model Name"
                            required
                            fullWidth
                            value={modelForm.underlyingModelName}
                            onChange={(e) => {
                                setModelForm((f) => ({
                                    ...f,
                                    underlyingModelName: e.target.value,
                                }));
                                resetInitialDeployTest();
                            }}
                        />
                        <FormControl>
                            <FormLabel
                                sx={{
                                    fontSize: "12px",
                                    color: theme.palette.text.primary,
                                }}
                            >
                                Purpose
                            </FormLabel>
                            <RadioGroup
                                row
                                value={modelForm.purpose}
                                onChange={(e) =>
                                    setModelForm((f) => ({
                                        ...f,
                                        purpose: e.target
                                            .value as LLMModelPurpose,
                                    }))
                                }
                            >
                                <FormControlLabel
                                    value={LLMModelPurpose.KBo}
                                    control={<Radio />}
                                    label="KBo"
                                />
                                <FormControlLabel
                                    value={LLMModelPurpose.ModelBuilder}
                                    control={<Radio />}
                                    label="Model Builder"
                                />
                            </RadioGroup>
                        </FormControl>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={modelForm.isThinkingModel}
                                    onChange={(e) => {
                                        setModelForm((f) => ({
                                            ...f,
                                            isThinkingModel: e.target.checked,
                                        }));
                                        resetInitialDeployTest();
                                    }}
                                />
                            }
                            label="Thinking Model"
                        />
                        {modelForm.isThinkingModel && (
                            <AcxMainTextField
                                id="model-thinking-effort"
                                labelText="Thinking Effort Levels (comma-separated)"
                                fullWidth
                                required
                                value={modelForm.thinkingEffortLevels}
                                error={
                                    parseThinkingEffortLevels(
                                        modelForm.thinkingEffortLevels,
                                    ).length === 0
                                }
                                errorHelper="Thinking effort levels are required"
                                onChange={(e) => {
                                    setModelForm((f) => ({
                                        ...f,
                                        thinkingEffortLevels: e.target.value,
                                    }));
                                    resetInitialDeployTest();
                                }}
                                helperText="e.g. none,low,medium,high,xhigh"
                            />
                        )}

                        {!editingModel && (
                            <Box
                                sx={{
                                    mt: 2,
                                    pt: 2,
                                    borderTop: `1px solid ${theme.palette.gray[200]}`,
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2,
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: "16px",
                                        fontWeight: 600,
                                        color: theme.palette.gray[700],
                                    }}
                                >
                                    First Deployment
                                </Typography>
                                <Box>
                                    <AcxSelect
                                        id="initial-deployment-host"
                                        inputLabel="Host"
                                        fullWidth
                                        required
                                        options={hostOptions}
                                        defaultValue={hostOptions.find(
                                            (o) =>
                                                o.value ===
                                                initialDeployForm.hostId,
                                        )}
                                        onChange={(opt) =>
                                            updateInitialDeployForm((f) => ({
                                                ...f,
                                                hostId: opt?.value ?? "",
                                                configurationValue: "",
                                            }))
                                        }
                                    />
                                </Box>
                                <AcxMainTextField
                                    id="initial-deployment-name"
                                    labelText={getDeploymentConfigLabel(
                                        getHostType(initialDeployForm.hostId),
                                    )}
                                    required
                                    fullWidth
                                    value={initialDeployForm.configurationValue}
                                    onChange={(e) =>
                                        updateInitialDeployForm((f) => ({
                                            ...f,
                                            configurationValue: e.target.value,
                                        }))
                                    }
                                    helperText={getDeploymentConfigHelperText(
                                        getHostType(initialDeployForm.hostId),
                                    )}
                                />
                                <AcxMainTextField
                                    id="initial-deployment-priority"
                                    labelText="Priority"
                                    type="number"
                                    fullWidth
                                    value={initialDeployForm.priority}
                                    onChange={(e) =>
                                        updateInitialDeployForm((f) => ({
                                            ...f,
                                            priority:
                                                parseInt(e.target.value, 10) ||
                                                0,
                                        }))
                                    }
                                />
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                    }}
                                >
                                    <AcxButton
                                        color="white"
                                        onClick={handleTestInitialDeployment}
                                        loading={initialDeployTesting}
                                        disabled={!canTestInitialDeployment}
                                    >
                                        Test
                                    </AcxButton>
                                    {!initialDeployTestResult && (
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            Run Test before creating. Create is
                                            disabled until a successful test.
                                        </Typography>
                                    )}
                                </Box>
                                {initialDeployTestResult && (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            p: 1,
                                            borderRadius: 1,
                                            backgroundColor:
                                                initialDeployTestResult.success
                                                    ? theme.palette.success
                                                          .light
                                                    : theme.palette.error.light,
                                            color: initialDeployTestResult.success
                                                ? theme.palette.success
                                                      .contrastText
                                                : theme.palette.error
                                                      .contrastText,
                                        }}
                                    >
                                        {initialDeployTestResult.success ? (
                                            <CheckCircleOutline fontSize="small" />
                                        ) : (
                                            <ErrorOutline fontSize="small" />
                                        )}
                                        <Typography variant="body2">
                                            {initialDeployTestResult.message}
                                            {initialDeployTestResult.latencyMs !=
                                                null &&
                                                ` (${initialDeployTestResult.latencyMs}ms)`}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        )}

                        {editingModel && (
                            <Box
                                sx={{
                                    mt: 2,
                                    pt: 2,
                                    borderTop: `1px solid ${theme.palette.gray[200]}`,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        mb: 1,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: "16px",
                                            fontWeight: 600,
                                            color: theme.palette.gray[700],
                                            marginRight: "auto",
                                        }}
                                    >
                                        Deployments
                                    </Typography>
                                    <AddButton onClick={openCreateDeployment}>
                                        Deployment
                                    </AddButton>
                                </Box>
                                {deploymentsForEditingModel.length === 0 ? (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ p: 1 }}
                                    >
                                        No deployments yet. Add one to make this
                                        model usable.
                                    </Typography>
                                ) : (
                                    <AcxTable
                                        columns={deploymentColumns}
                                        rows={deploymentsForEditingModel}
                                        enableHover
                                        cellDense
                                    />
                                )}
                            </Box>
                        )}
                    </Box>
                }
            >
                <AcxButton
                    color="primary"
                    onClick={handleSaveModel}
                    loading={modelSaving}
                    disabled={!canSaveModel}
                >
                    {editingModel ? "Update" : "Create"}
                </AcxButton>
            </AcxDialog>

            {/* Deployment Create/Edit Dialog */}
            <AcxDialog
                title={
                    editingDeployment ? "Edit Deployment" : "Add Deployment"
                }
                isOpen={deployDialogOpen}
                onClose={() => setDeployDialogOpen(false)}
                maxWidth={false}
                contentWidth="min(800px, calc(100vw - 96px))"
                dialogContentChildren={
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                            pt: 1,
                        }}
                    >
                        <Box>
                            <AcxSelect
                                id="deployment-host"
                                inputLabel="Host"
                                fullWidth
                                required
                                options={hostOptions}
                                defaultValue={hostOptions.find(
                                    (o) => o.value === deployForm.hostId,
                                )}
                                onChange={(opt) =>
                                    updateDeployForm((f) => ({
                                        ...f,
                                        hostId: opt?.value ?? "",
                                        configurationValue: "",
                                    }))
                                }
                            />
                        </Box>
                        <AcxMainTextField
                            id="deployment-name"
                            labelText={getDeploymentConfigLabel(
                                getHostType(deployForm.hostId),
                            )}
                            required
                            fullWidth
                            value={deployForm.configurationValue}
                            onChange={(e) =>
                                updateDeployForm((f) => ({
                                    ...f,
                                    configurationValue: e.target.value,
                                }))
                            }
                            helperText={getDeploymentConfigHelperText(
                                getHostType(deployForm.hostId),
                            )}
                        />
                        <AcxMainTextField
                            id="deployment-priority"
                            labelText="Priority"
                            type="number"
                            fullWidth
                            value={deployForm.priority}
                            onChange={(e) =>
                                updateDeployForm((f) => ({
                                    ...f,
                                    priority:
                                        parseInt(e.target.value, 10) || 0,
                                }))
                            }
                        />

                        {deployTestResult && (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    p: 1,
                                    borderRadius: 1,
                                    backgroundColor: deployTestResult.success
                                        ? theme.palette.success.light
                                        : theme.palette.error.light,
                                    color: deployTestResult.success
                                        ? theme.palette.success.contrastText
                                        : theme.palette.error.contrastText,
                                }}
                            >
                                {deployTestResult.success ? (
                                    <CheckCircleOutline fontSize="small" />
                                ) : (
                                    <ErrorOutline fontSize="small" />
                                )}
                                <Typography variant="body2">
                                    {deployTestResult.message}
                                    {deployTestResult.latencyMs != null &&
                                        ` (${deployTestResult.latencyMs}ms)`}
                                </Typography>
                            </Box>
                        )}
                        {!deployTestResult && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                Run Test to validate the configuration. Save is
                                disabled until a successful test against the
                                current values.
                            </Typography>
                        )}
                    </Box>
                }
            >
                <AcxButton
                    color="white"
                    onClick={handleTestDeploymentForm}
                    loading={deployTesting}
                    disabled={!canTestDeployment}
                >
                    Test
                </AcxButton>
                <AcxButton
                    color="primary"
                    onClick={handleSaveDeployment}
                    loading={deploySaving}
                    disabled={!canSaveDeployment}
                >
                    {editingDeployment ? "Update" : "Create"}
                </AcxButton>
            </AcxDialog>

            {/* Delete Model Confirmation */}
            <AcxDialog
                title="Delete LLM Model"
                text="Are you sure? Models with active deployments cannot be deleted."
                isOpen={deleteModel !== null}
                onClose={() => setDeleteModel(null)}
                contentWidth="400px"
                dialogContentChildren={
                    deleteModel && (
                        <Typography
                            sx={{
                                color: theme.palette.gray[500],
                                fontWeight: 600,
                            }}
                        >
                            - {deleteModel.name}
                        </Typography>
                    )
                }
            >
                <AcxButton
                    color="primary"
                    onClick={() =>
                        deleteModel && handleDeleteModel(deleteModel.id)
                    }
                >
                    Delete
                </AcxButton>
            </AcxDialog>

            {/* Delete Deployment Confirmation */}
            <AcxDialog
                title="Delete Deployment"
                text="Are you sure you want to delete this deployment?"
                isOpen={deleteDeployment !== null}
                onClose={() => setDeleteDeployment(null)}
                contentWidth="400px"
                dialogContentChildren={
                    deleteDeployment && (
                        <Typography
                            sx={{
                                color: theme.palette.gray[500],
                                fontWeight: 600,
                            }}
                        >
                            - {deleteDeployment.hostName} (
                            {String(
                                deleteDeployment.configuration
                                    ?.DeploymentName ??
                                    deleteDeployment.configuration
                                        ?.deploymentName ??
                                    deleteDeployment.id.substring(0, 8),
                            )}
                            )
                        </Typography>
                    )
                }
            >
                <AcxButton
                    color="primary"
                    onClick={() =>
                        deleteDeployment &&
                        handleDeleteDeployment(deleteDeployment.id)
                    }
                >
                    Delete
                </AcxButton>
            </AcxDialog>
        </Box>
    );
});

export default LLMModelsTable;
